import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  API,
  authHeaders,
  alertError,
  alertSuccess,
  btnPrimary,
  fieldStyle,
  labelStyle,
  pageTitleStyle,
  pageSubtitleStyle,
} from '../api/ticketsApi';
import SearchableSelect, { ticketsToOptions } from '../../../components/SearchableSelect';
import { ensureTicketInList, usePrefillTicketNo } from '../hooks/usePrefillTicketNo';
import { TicketActionBack } from '../ticketNav';
import { useProductFilter } from '../ProductFilterContext';

function AssigneePicker({ assignees, selected, onSelect, label = 'Assignees' }) {
  return (
    <div>
      <label className="tk-label" style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {assignees.length === 0 ? (
          <span style={{ color: '#74777F', fontSize: '13px' }}>Select a ticket to load team members.</span>
        ) : (
          assignees.map((a) => {
            const userId = a.user_id || a;
            const name = a.name || a;
            const workload = a.workload ?? a.open_count ?? 0;
            return (
              <label
                key={userId}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 12px',
                  border: selected === userId ? '2px solid #002FA7' : '1px solid #C9CED8',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  background: selected === userId ? '#EEF0FF' : 'white',
                  fontSize: '13px',
                }}
              >
                <input
                  type="radio"
                  name="transfer-assignee"
                  checked={selected === userId}
                  onChange={() => onSelect(userId)}
                />
                {name} ({workload})
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

function TicketTransferPage() {
  const prefillTicket = usePrefillTicketNo();
  const { productId, selectedProduct } = useProductFilter();
  const [tickets, setTickets] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(prefillTicket);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const filteredTickets = useMemo(
    () => (productId ? tickets.filter((t) => t.product_id === productId) : tickets),
    [tickets, productId],
  );

  const ticketOptions = useMemo(
    () => ticketsToOptions(filteredTickets, (t) => (
      t.assignee_names?.length ? t.assignee_names.join(', ') : ''
    )),
    [filteredTickets],
  );

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/assignments/in-process`, {
        ...authHeaders(),
        params: productId ? { product_id: productId } : undefined,
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setTickets(list);
      if (prefillTicket) {
        setSelectedTicket(prefillTicket);
        if (!list.some((t) => t.ticket_no === prefillTicket)) {
          await ensureTicketInList(prefillTicket, setTickets, {
            filter: (t) => !['closed', 'cancelled', 'completed'].includes(t.status),
          });
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load in-process tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, [prefillTicket, productId]);

  useEffect(() => {
    if (!selectedTicket) {
      setAssignees([]);
      setSelectedAssignee('');
      return;
    }
    let cancelled = false;
    axios.get(`${API}/assignments/downstream-assignees?ticket_no=${selectedTicket}`, authHeaders())
      .then(async (r) => {
        if (cancelled) return;
        let list = Array.isArray(r.data) ? r.data : [];
        // Empty list is a successful response — fall back to team engineers.
        if (!list.length) {
          try {
            const eng = await axios.get(
              `${API}/assignments/engineers?ticket_no=${selectedTicket}`,
              authHeaders(),
            );
            list = Array.isArray(eng.data) ? eng.data : [];
          } catch {
            list = [];
          }
        }
        setAssignees(list);
        setSelectedAssignee('');
      })
      .catch(async () => {
        if (cancelled) return;
        try {
          const eng = await axios.get(
            `${API}/assignments/engineers?ticket_no=${selectedTicket}`,
            authHeaders(),
          );
          if (!cancelled) setAssignees(eng.data || []);
        } catch {
          if (!cancelled) setAssignees([]);
        }
      });
    return () => { cancelled = true; };
  }, [selectedTicket]);

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!selectedTicket || !selectedAssignee) {
      setError('Select a ticket and one assignee');
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      await axios.post(
        `${API}/assignments/transfer`,
        { ticket_no: selectedTicket, assignee_ids: [selectedAssignee], reason: reason || null },
        authHeaders(),
      );
      setSuccess(`Ticket ${selectedTicket} transferred`);
      setSelectedTicket('');
      setSelectedAssignee('');
      setReason('');
      await loadTickets();
    } catch (err) {
      setError(err.response?.data?.detail || 'Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const ticket = tickets.find((t) => t.ticket_no === selectedTicket);

  if (loading) return <p style={{ color: '#74777F' }}>Loading transferable tickets…</p>;

  return (
    <div style={{ maxWidth: '960px' }}>
      <div className="tk-page-head">
        <div className="tk-page-head-main">
          <div className="tk-page-head-back">
            <TicketActionBack ticketNo={selectedTicket || prefillTicket || null} />
          </div>
          <h2 style={pageTitleStyle}>Transfer Ticket</h2>
          <p style={pageSubtitleStyle}>
            Reassign open, pending, or in-progress tickets to another team member.
            {selectedProduct ? ` Scoped to ${selectedProduct.name}.` : ''}
          </p>
        </div>
      </div>

      <form onSubmit={handleTransfer} style={{ display: 'grid', gap: '16px' }}>
        {error && <div style={alertError}>{typeof error === 'string' ? error : JSON.stringify(error)}</div>}
        {success && <div style={alertSuccess}>{success}</div>}

        <div>
          <label className="tk-label" style={labelStyle}>Transferable ticket ({tickets.length})</label>
          <SearchableSelect
            options={ticketOptions}
            value={selectedTicket}
            onChange={setSelectedTicket}
            variant="ticket"
            placeholder="Search and select a ticket…"
            searchPlaceholder="Search by ticket no, subject, assignee…"
            aria-label="Transfer ticket"
          />
        </div>

        {ticket && (
          <div className="tk-field-readonly" style={{ padding: '12px', fontSize: '13px' }}>
            <strong>{ticket.subject}</strong>
            {ticket.assignee_names && (
              <p style={{ margin: '8px 0 0', color: '#44474E' }}>
                Current assignees: {ticket.assignee_names.join(', ')}
              </p>
            )}
          </div>
        )}

        <AssigneePicker assignees={assignees} selected={selectedAssignee} onSelect={setSelectedAssignee} label="Transfer to (select one)" />

        <div>
          <label className="tk-label" style={labelStyle}>Transfer Reason</label>
          <textarea className="tk-field" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} style={fieldStyle} />
        </div>

        <button
          type="submit"
          disabled={submitting || !selectedTicket || !selectedAssignee}
          className="tk-btn tk-btn-primary"
          style={{ ...btnPrimary, width: 'fit-content', opacity: submitting ? 0.7 : 1 }}
        >
          {submitting ? 'Transferring…' : 'Transfer Ticket'}
        </button>
      </form>
    </div>
  );
}

export default TicketTransferPage;
